import React, { useEffect, useState, useCallback } from "react"
import dynamic from "next/dynamic"
const ConnectAccountModal = dynamic(() => import("../components/presentational/tailwind/ConnectAccountModal"), { ssr: false })
const UpgradeModal = dynamic(() => import("../components/presentational/tailwind/UpgradeModal"), { ssr: false })

const UPGRADE_MODAL_DISMISSED_KEY = "outfame_upgrade_modal_dismissed_at"

/**
 * Modal Context Types
 *
 * openUpgradeModal: Opens the upgrade modal with optional account context
 * - If accountId provided: Modal will prioritize showing that account (unless on Glory)
 * - If no accountId: Modal finds the most recent upgradeable account
 *
 * setTrialExpired: Set whether trial is expired
 * - When true: Modal auto-opens for expired trial accounts but can be dismissed
 */
interface ModalContextValue {
    setIsConnectAccountModalOpened: (open: boolean) => void
    setConnectAccountModalInitialPlatform: (platform: "instagram" | "tiktok") => void
    openUpgradeModal: (accountId?: string) => void
    closeUpgradeModal: () => void
    dismissUpgradeModal: () => void
    setTrialExpired: (expired: boolean) => void
    isTrialExpired: boolean
    // Legacy - kept for backward compatibility during migration
    setIsUpgradeModalOpened: (open: boolean) => void
}

const ModalContext = React.createContext<ModalContextValue | null>(null)
ModalContext.displayName = "ModalContext"
export const useModalContext = () => React.useContext(ModalContext) || ({} as ModalContextValue)

export default function ModalContextProvider({ children }) {
    const [isConnectAccountModalOpened, setIsConnectAccountModalOpened] = React.useState(false)
    const [isConnectAccountModalLoaded, setIsConnectAccountModalLoaded] = useState(false)
    const [connectAccountModalInitialPlatform, setConnectAccountModalInitialPlatform] = useState<"instagram" | "tiktok">("instagram")

    const [isUpgradeModalOpened, setIsUpgradeModalOpenedInternal] = React.useState(false)
    const [isUpgradeModalLoaded, setIsUpgradeModalLoaded] = useState(false)

    /**
     * Trial expired state - when true, modal cannot be dismissed
     * Set by AccountClientLayout when it detects an expired trial
     */
    const [isTrialExpiredState, setTrialExpiredState] = useState(false)

    /**
     * Account ID passed to upgrade modal from trigger context
     * - When set: Modal prioritizes this account (if upgradeable)
     * - When null: Modal finds most recent upgradeable account
     * - Reset to null when modal closes
     */
    const [upgradeModalAccountId, setUpgradeModalAccountId] = useState<string | null>(null)

    useEffect(() => {
        const loadModals = () => {
            setIsConnectAccountModalLoaded(true)
            setIsUpgradeModalLoaded(true)
        }

        "requestIdleCallback" in window ? window.requestIdleCallback(loadModals) : setTimeout(loadModals, 1)
    }, [])

    /**
     * Opens the upgrade modal with optional account context
     * @param accountId - Optional account ID from the trigger context (URL, props, etc.)
     */
    const openUpgradeModal = useCallback((accountId?: string) => {
        setUpgradeModalAccountId(accountId || null)
        setIsUpgradeModalOpenedInternal(true)
    }, [])

    /**
     * Closes the upgrade modal and resets account context
     */
    const closeUpgradeModal = useCallback(() => {
        setIsUpgradeModalOpenedInternal(false)
        // Reset account ID after modal closes to ensure fresh state on next open
        setUpgradeModalAccountId(null)
    }, [])

    /**
     * Dismisses the upgrade modal and records timestamp for cooldown
     * Used for aggressive trial modal - tracks when user explicitly dismisses
     */
    const dismissUpgradeModal = useCallback(() => {
        localStorage.setItem(UPGRADE_MODAL_DISMISSED_KEY, Date.now().toString())
        closeUpgradeModal()
    }, [closeUpgradeModal])

    /**
     * Set trial expired state - used by AccountClientLayout
     */
    const setTrialExpired = useCallback((expired: boolean) => {
        setTrialExpiredState(expired)
    }, [])

    /**
     * Legacy setter - kept for backward compatibility
     * New code should use openUpgradeModal(accountId) instead
     */
    const setIsUpgradeModalOpened = useCallback((open: boolean) => {
        if (open) {
            openUpgradeModal()
        } else {
            closeUpgradeModal()
        }
    }, [openUpgradeModal, closeUpgradeModal])

    const value: ModalContextValue = {
        setIsConnectAccountModalOpened,
        setConnectAccountModalInitialPlatform,
        openUpgradeModal,
        closeUpgradeModal,
        dismissUpgradeModal,
        setTrialExpired,
        isTrialExpired: isTrialExpiredState,
        setIsUpgradeModalOpened
    }

    return (
        <ModalContext.Provider value={value}>
            {children}
            {isConnectAccountModalLoaded && (
                <ConnectAccountModal
                    isOpen={isConnectAccountModalOpened}
                    onClose={() => {
                        setIsConnectAccountModalOpened(false)
                    }}
                    initialPlatform={connectAccountModalInitialPlatform}
                />
            )}
            {isUpgradeModalLoaded && (
                <UpgradeModal
                    isOpen={isUpgradeModalOpened}
                    onClose={dismissUpgradeModal}
                    accountId={upgradeModalAccountId}
                />
            )}
        </ModalContext.Provider>
    )
}
